import { useState } from "react";
import { ImageOff, PlayCircle } from "lucide-react";
import VideoPreview from "@/components/VideoPreview";
import CopyForNotesButton from "@/components/CopyForNotesButton";
import { prescriptionLoadLabel } from "@/lib/patientPrescription";
import "@/components/exerciseWorksheet.css";

const VALID_YOUTUBE_ID = /^[A-Za-z0-9_-]{11}$/;

function safeHttpUrl(value) {
  try {
    const parsed = new URL(value);
    return ["https:", "http:"].includes(parsed.protocol) && !parsed.username ? parsed.href : "";
  } catch {
    return "";
  }
}

function youtubeId(exercise) {
  if (VALID_YOUTUBE_ID.test(exercise.youtube_video_id || "")) return exercise.youtube_video_id;
  const url = safeHttpUrl(exercise.video_url);
  if (!url) return "";
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, "");
    if (host === "youtu.be") return VALID_YOUTUBE_ID.test(parsed.pathname.slice(1)) ? parsed.pathname.slice(1) : "";
    if (["youtube.com", "m.youtube.com"].includes(host)) {
      const candidate = parsed.pathname.startsWith("/embed/")
        ? parsed.pathname.split("/")[2]
        : parsed.searchParams.get("v");
      return VALID_YOUTUBE_ID.test(candidate || "") ? candidate : "";
    }
  } catch {}
  return "";
}

export function exerciseVideoUrl(exercise) {
  const url = safeHttpUrl(exercise.video_url);
  if (url) return url;
  const id = youtubeId(exercise);
  return id ? `https://www.youtube.com/watch?v=${id}` : "";
}

export function exerciseImageUrl(exercise) {
  const thumbnail = safeHttpUrl(exercise.thumbnail_url);
  if (thumbnail) return thumbnail;
  const id = youtubeId(exercise);
  return id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : "";
}

export function instructionParts(exercise) {
  const source = String(exercise.instructions || exercise.description || "").trim();
  if (!source) return { setup: "—", movement: "—" };
  const lines = source.split(/\n+/).map((part) => part.trim()).filter(Boolean);
  if (lines.length > 1) return { setup: lines[0], movement: lines.slice(1).join(" ") };
  const sentences = source.match(/[^.!?]+[.!?]+|[^.!?]+$/g)?.map((part) => part.trim()).filter(Boolean) || [];
  if (sentences.length > 1) return { setup: sentences[0], movement: sentences.slice(1).join(" ") };
  const split = source.match(/^(.{12,100}?)[;:]\s+(.+)$/);
  return split ? { setup: split[1], movement: split[2] } : { setup: "—", movement: source };
}

export function dosageSummary(exercise) {
  const parts = [];
  const load = prescriptionLoadLabel(exercise);
  if (load) parts.push(load);
  const sets = String(exercise.sets || "").trim();
  const reps = String(exercise.reps || "").trim();
  const hold = String(exercise.hold_time || "").trim();
  if (sets && reps) parts.push(`${sets} sets × ${reps} reps`);
  else if (sets) parts.push(`${sets} sets`);
  else if (reps) parts.push(hold ? `${reps} × ${hold} holds` : `${reps} reps`);
  if (hold && !(reps && !sets)) parts.push(`${hold} hold`);
  if (exercise.frequency) parts.push(exercise.frequency);
  return parts.join(" | ") || "Dosage as advised";
}

export default function ExerciseWorksheet({ program, showVideos = false, showNoteCopy = false }) {
  return <article className="exercise-worksheet rounded-2xl border border-slate-200 bg-white p-6 text-slate-900" aria-label="Exercise worksheet preview">
    <header className="worksheet-heading border-b-2 border-teal-600 pb-4">
      <p className="worksheet-brand font-display text-xl font-bold text-teal-700">Bexley Road Physiotherapy &amp; Sports Injury Centre</p>
      <h1 className="mt-1 text-lg font-semibold">Home Exercise Program</h1>
      <dl className="worksheet-patient-details mt-4 grid gap-2 text-sm sm:grid-cols-4">
        <Detail label="Patient" value={program.patient_name || "Patient name"} />
        <Detail label="Clinician" value={program.clinician_name || "Clinician name"} />
        <Detail label="Date" value={program.worksheet_date || "—"} />
        {program.review_date && <Detail label="Review date" value={program.review_date} />}
      </dl>
    </header>
    <div className="worksheet-exercise-list">
      {program.exercises.map((exercise, index) => {
        const video = exerciseVideoUrl(exercise);
        const instructions = instructionParts(exercise);
        return <section className="worksheet-exercise border-b border-slate-200" key={`${exercise.exercise_id}-${index}`}>
          <ExerciseImage exercise={exercise} video={video} />
          <div className="worksheet-exercise-details min-w-0">
            <div className="flex flex-wrap items-start justify-between gap-2"><h2 className="worksheet-exercise-title text-lg font-bold">{index + 1}. {exercise.title}</h2>{showNoteCopy && <CopyForNotesButton prescription={exercise} compact />}</div>
            <p className="worksheet-dosage mt-1 rounded-lg bg-teal-50 px-3 py-2 text-sm font-bold text-teal-900">{dosageSummary(exercise)}</p>
            <div className="worksheet-instructions mt-3 grid gap-3 text-sm sm:grid-cols-2">
              <Text label="Setup" value={instructions.setup} />
              <Text label="Movement" value={instructions.movement} />
            </div>
            <Text label="Key cue / tip" value={exercise.key_cues} compact />
            {exercise.clinician_notes && <Text label="Clinician notes" value={exercise.clinician_notes} compact />}
            {exercise.safety_notes && <Text label="Safety notes" value={exercise.safety_notes} compact />}
            {video ? <p className="worksheet-video-row mt-3 text-sm">
              <PlayCircle aria-hidden="true" className="inline h-4 w-4 align-[-2px]" />{" "}
              <a className="worksheet-video-link font-semibold text-teal-700 underline" href={video} target="_blank" rel="noreferrer">Watch exercise video</a>
            </p> : <p className="mt-3 text-sm text-slate-500">Video not available for this exercise.</p>}
            {showVideos && video && <details className="worksheet-player mt-3">
              <summary className="cursor-pointer text-sm font-semibold text-teal-700">Play video</summary>
              <div className="mt-2"><VideoPreview url={video} title={exercise.title} /></div>
            </details>}
          </div>
        </section>;
      })}
    </div>
    {program.notes && <section className="worksheet-program-notes pt-4"><Text label="Program notes" value={program.notes} /></section>}
  </article>;
}

function ExerciseImage({ exercise, video }) {
  const [failed, setFailed] = useState(false);
  const image = failed ? "" : exerciseImageUrl(exercise);
  const content = image
    ? <img src={image} alt={`${exercise.title} exercise`} onError={() => setFailed(true)} />
    : <span className="worksheet-image-placeholder"><ImageOff aria-hidden="true" /><span>Image unavailable</span></span>;
  return <div className="worksheet-image-column">
    {video ? <a className="worksheet-image-frame" href={video} target="_blank" rel="noreferrer" aria-label={`Watch ${exercise.title} video`}>{content}</a>
      : <div className="worksheet-image-frame">{content}</div>}
  </div>;
}

function Detail({ label, value }) {
  return <div><dt className="font-semibold text-slate-600">{label}</dt><dd className="font-medium">{value}</dd></div>;
}

function Text({ label, value, compact = false }) {
  return <div className={compact ? "mt-2 text-sm" : "text-sm"}><h3 className="font-semibold text-slate-700">{label}</h3><p className="whitespace-pre-wrap break-words">{value || "—"}</p></div>;
}
